import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useCodeInput } from './useCodeInput'
import { useResendCountdown, saveNextResend, readNextResend } from '../../model/useResendTimer'
import styles from './CodeStep.module.css'

const CODE_LENGTH = 6
const ERROR_AUTOHIDE_MS = 3000

interface CodeStepProps {
  /** Email, на который отправлен код (для подзаголовка) */
  email: string
  /** Обработчик полного кода (после автозаполнения) */
  onSubmit: (code: string) => Promise<void>
  /** Запрос повторной отправки кода */
  onResend: () => Promise<void>
  /** Возврат к шагу ввода email */
  onChangeEmail: () => void
  /** Серверная ошибка */
  errorText?: string | null
  /** Оставшееся число попыток (для отображения) */
  attemptsLeft?: number | null
  /** Заблокировать ввод (лимит попыток / истёкший код) */
  isLocked?: boolean
  /** ISO-время, когда можно снова нажать "Отправить повторно" */
  nextResendAt?: string | null
  /** Идёт ли проверка кода */
  isSubmitting?: boolean
  /** Триггер shake-анимации + очистки полей */
  shakeSignal?: number
}

/**
 * Шаг ввода 6-значного кода из email: отдельные ячейки с paste, автопереходом,
 * автоотправкой по заполнении; shake и очистка при неверном коде; таймер resend.
 * @param email - Email пользователя для подзаголовка
 * @param onSubmit - Колбэк полного кода
 * @param onResend - Запрос повторной отправки
 * @param onChangeEmail - Возврат к предыдущему шагу
 * @param errorText - Серверная ошибка
 * @param attemptsLeft - Оставшееся число попыток
 * @param isLocked - Заблокировать поля
 * @param nextResendAt - ISO, когда можно запросить повторно
 * @param isSubmitting - Идёт ли отправка
 * @param shakeSignal - Триггер shake-анимации
 */
export const CodeStep = ({
  email,
  onSubmit,
  onResend,
  onChangeEmail,
  errorText = null,
  attemptsLeft = null,
  isLocked = false,
  nextResendAt = null,
  isSubmitting = false,
  shakeSignal = 0
}: CodeStepProps) => {
  const [resetSignal, setResetSignal] = useState(0)
  const { digits, inputsRef, shakeControls, handleChange, handleKeyDown, handlePaste } =
    useCodeInput({ length: CODE_LENGTH, onComplete: onSubmit, shakeSignal, resetSignal })

  const handleResend = async () => {
    setResetSignal((x) => x + 1)
    await onResend()
  }

  const effectiveResendAt = nextResendAt ?? readNextResend()
  const resendIn = useResendCountdown(effectiveResendAt)

  useEffect(() => {
    if (nextResendAt) saveNextResend(nextResendAt)
  }, [nextResendAt])

  // Автоскрытие ошибки через 3с (кроме "залипших" типа истёк/лимит — когда isLocked).
  const [visibleError, setVisibleError] = useState<string | null>(errorText)
  useEffect(() => {
    setVisibleError(errorText ?? null)
    if (!errorText || isLocked) return
    const id = window.setTimeout(() => setVisibleError(null), ERROR_AUTOHIDE_MS)
    return () => window.clearTimeout(id)
  }, [errorText, isLocked])

  const canResend = resendIn === 0 && !isSubmitting
  const cellClass = [styles.cell, visibleError && styles.invalid].filter(Boolean).join(' ')

  return (
    <div className={styles.step}>
      <div className={styles.header}>
        <div className={styles.info}>
          <h1 className={styles.title}>Введите код</h1>
          <div className={styles.subtitle}>
            <p className={styles.subtitleText}>Выслали на {email}</p>
            <button type="button" onClick={onChangeEmail} className={styles.changeEmail}>
              Изменить
            </button>
          </div>
        </div>

        <motion.div animate={shakeControls} className={styles.cells}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputsRef.current[i] = el }}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              inputMode="numeric"
              // OTP-autofill вешаем только на первую ячейку (iOS требует один
              // таргет); maxLength у неё снят, чтобы autofill вставил весь код
              // целиком — handleChange распределит его по ячейкам.
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={i === 0 ? CODE_LENGTH : 1}
              disabled={isLocked || isSubmitting}
              className={cellClass}
            />
          ))}
        </motion.div>

        {visibleError && (
          <div className={styles.error}>
            {visibleError}
            {attemptsLeft !== null && attemptsLeft > 0 && ` Осталось попыток: ${attemptsLeft}`}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => void handleResend()}
        disabled={!canResend}
        className={styles.resend}
      >
        <span>Отправить код повторно</span>
        {resendIn > 0 && <span className={styles.resendTimer}>{resendIn}</span>}
      </button>
    </div>
  )
}
